import { useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { DocumentView, MiButton, MiIcon } from "@miplatform/components";
import {
  ICONS_WEB,
  ISSUE_STATUSES_KEYED,
  ORIENTATION,
} from "@miplatform/constants";
import { isEmpty } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Divider, Typography } from "@mui/material";

const PLACEHOLDER_TEXT = "---";

const getTaskCardDescription = (issue) => {
  if (issue.issueResult?.originalDescription) {
    return issue.issueResult.originalDescription;
  }

  if (issue.originalDescription) {
    return issue.originalDescription;
  }

  if (!isEmpty(issue?.issueResult?.description)) {
    return issue.issueResult.description;
  }

  if (!isEmpty(issue?.issueResult?.repairDescription)) {
    return issue.issueResult.repairDescription;
  }

  return issue.description;
};

export const IssueCard = ({
  issue,
  issueIndex,
  testId,
  cardBgColor = null,
  iconBgColor,
  customClass,
  handleEdit,
  handleDelete,
  readOnly = false,
  length,
}) => {
  const { classes, theme } = useStyles({
    iconBgColor,
    cardBgColor,
    issueStatus: issue.status,
  });

  const [documentInView, setDocumentInView] = useState({});

  const tempURL = useMemo(() => {
    if (!isEmpty(documentInView)) {
      return URL.createObjectURL(documentInView);
    }
    return null;
  }, [documentInView]);

  return (
    <Box
      data-testId={testId ?? "TaskCardItem"}
      className={[classes.taskCardContainer, customClass]}
    >
      <Typography
        className={classes.issueIndex}
        minWidth={length > 9 ? 16 : undefined}
      >
        {issueIndex}
      </Typography>
      <Box className={classes.detailsWrapper}>
        <Box>
          <Box className={classes.taskDetails}>
            <Typography
              className={classes.cardTitle}
              data-testid={`issue-title-${issueIndex}`}
            >
              {issue.title}
            </Typography>
            <Typography
              className={classes.fieldsets}
              data-testid={`issue-area-${issueIndex}`}
            >
              {issue.areaName || PLACEHOLDER_TEXT}
            </Typography>
            <Divider
              orientation={ORIENTATION.VERTICAL}
              variant="middle"
              flexItem
            />
            <Typography
              className={classes.fieldsets}
              style={{
                color: ISSUE_STATUSES_KEYED[issue.status].color,
                fontWeight: theme.fontWeight.semiBold,
              }}
              data-testid={`issue-status-${issueIndex}`}
            >
              {ISSUE_STATUSES_KEYED[issue.status].label || PLACEHOLDER_TEXT}
            </Typography>
          </Box>
          <Typography
            className={classes.description}
            data-testid={`issue-description-${issueIndex}`}
          >
            {getTaskCardDescription(issue)}
          </Typography>
          {!isEmpty(documentInView) && (
            <Box
              className={classes.ducumentView}
              onClick={(e) => {
                e.stopPropagation();
                setDocumentInView({});
              }}
            >
              <DocumentView
                document={{
                  ...documentInView,
                  tempURL,
                  contentType: documentInView.type,
                }}
              />
            </Box>
          )}
          <Box display="flex" gap={2}>
            {issue.issueDocuments?.map((doc, index) => (
              <Box
                key={`${doc.name}-${index}`}
                className={classes.issueDocumentIndicator}
                onClick={() => {
                  setDocumentInView(doc);
                }}
              >
                <MiIcon
                  path={mdi.mdiFileDocumentOutline}
                  color={theme.palette.primary.main}
                  size={0.5}
                />
                <Typography
                  color={theme.palette.primary.main}
                  fontSize={theme.fontSize.small}
                >
                  {doc.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box display="flex">
          {handleEdit && !readOnly && (
            <MiButton
              startIcon={{
                path: mdi[ICONS_WEB.PENCIL],
                iconColor: theme.palette.grey.medium,
              }}
              data-testid={`edit-issue-button-${issueIndex}`}
              className={classes.issueCardButton}
              onClick={handleEdit}
            />
          )}
          {handleDelete && (
            <MiButton
              startIcon={{
                path: mdi[ICONS_WEB.TRASH_CAN],
                iconColor: theme.palette.error.main,
              }}
              onClick={handleDelete}
              data-testid={`delete-issue-button-${issueIndex}`}
              className={classes.issueCardButton}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()(
  (theme, { iconBgColor, cardBgColor, issueStatus }) => ({
    taskCardContainer: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(2),
      minHeight: 68,
    },
    cardTitle: {
      fontWeight: theme.fontWeight.semiBold,
      fontSize: theme.fontSize.medium,
    },
    issueIndex: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.bold,
      color: theme.palette.primary.main,
    },
    icon: {
      backgroundColor: iconBgColor,
      borderRadius: theme.spacing(0.5),
      padding: iconBgColor ? theme.spacing(0.5) : 0,
    },
    detailsWrapper: {
      display: "flex",
      justifyContent: "space-between",
      flex: 1,
      backgroundColor:
        cardBgColor ?? ISSUE_STATUSES_KEYED[issueStatus]?.backgroundColor,
      border: `1px solid ${ISSUE_STATUSES_KEYED[issueStatus]?.color}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
    },
    fieldsets: {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.semiBold,
      color: theme.palette.grey.medium,
    },
    taskDetails: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    description: {
      ...theme.typography.body2,
      color: theme.palette.grey.dark,
      fontSize: theme.fontSize.small,
      minHeight: 20,
      textAlign: "justify",
    },
    issueCardButton: {
      height: "fit-content",
      padding: theme.spacing(1),
      borderRadius: "100%",
    },
    issueDocumentIndicator: {
      display: "flex",
      alignItems: "center",
      cursor: "pointer",
      gap: theme.spacing(0.5),
    },
    ducumentView: {
      zIndex: theme.zIndex.popper,
      width: "100%",
      height: "100%",
      position: "fixed",
      top: 0,
      left: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.5)",
    },
  })
);
